import { useEffect, useState } from "react";
import { Loader2, Building2, Layers } from "lucide-react";
import { toast } from "sonner";
import { staffApi, fmtErr } from "../api";

const Branches = () => {
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [branchForm, setBranchForm] = useState({ name: "", latitude: "", longitude: "", radius_m: "100" });
  const [deptForm, setDeptForm] = useState({ name: "", parent_id: "" });
  const [busy, setBusy] = useState("");
  const [formError, setFormError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [b, d] = await Promise.allSettled([staffApi.get("/branches"), staffApi.get("/departments")]);
      if (b.status === "fulfilled") setBranches(Array.isArray(b.value.data) ? b.value.data : []);
      if (d.status === "fulfilled") setDepartments(Array.isArray(d.value.data) ? d.value.data : []);
      if (b.status === "rejected" && d.status === "rejected") setError(fmtErr(b.reason));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleBranch = async (b) => {
    try {
      await staffApi.patch(`/branches/${b.id}`, { active: !b.active });
      toast.success(`${b.name} ${b.active ? "deactivated" : "activated"}.`);
      await load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const addBranch = async (e) => {
    e.preventDefault();
    setFormError("");
    setBusy("branch");
    try {
      await staffApi.post("/branches", {
        name: branchForm.name.trim(),
        latitude: parseFloat(branchForm.latitude),
        longitude: parseFloat(branchForm.longitude),
        radius_m: parseFloat(branchForm.radius_m) || 100,
      });
      toast.success("Branch added.");
      setBranchForm({ name: "", latitude: "", longitude: "", radius_m: "100" });
      await load();
    } catch (err) {
      setFormError(fmtErr(err));
    } finally {
      setBusy("");
    }
  };

  const addDept = async (e) => {
    e.preventDefault();
    setFormError("");
    setBusy("dept");
    try {
      await staffApi.post("/departments", { name: deptForm.name.trim(), parent_id: deptForm.parent_id || null });
      toast.success("Department added.");
      setDeptForm({ name: "", parent_id: "" });
      await load();
    } catch (err) {
      setFormError(fmtErr(err));
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="admin-branches">
      <h1 className="font-display text-2xl font-bold text-slate-900">Branches & Departments</h1>
      <p className="mt-1 text-sm text-slate-500">Branch geofences and department structure. Deletion is intentionally not available here.</p>
      {loading ? (
        <p className="mt-10 flex items-center gap-2 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> Loading…</p>
      ) : error ? (
        <p className="mt-6 text-xs text-[#C5221F] bg-red-50 border border-red-200 px-3 py-2.5 rounded-sm" data-testid="branches-error">{error}</p>
      ) : (
        <div className="mt-8 grid lg:grid-cols-2 gap-6">
          <div className="bg-white border border-slate-200 p-6" data-testid="branches-card">
            <p className="flex items-center gap-2 font-mono-tech text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">
              <Building2 size={14} className="text-[#C5221F]" /> Branches
            </p>
            <ul className="mt-4 divide-y divide-slate-100" data-testid="branches-list">
              {branches.map((b) => (
                <li key={b.id} className="py-3 flex items-center justify-between gap-3" data-testid={`branch-row-${b.id}`}>
                  <div>
                    <p className="text-sm font-medium text-slate-800">{b.name}</p>
                    <p className="text-xs text-slate-400">radius {b.radius_m}m · {Number(b.latitude).toFixed(4)}, {Number(b.longitude).toFixed(4)}</p>
                  </div>
                  <button onClick={() => toggleBranch(b)} data-testid={`branch-toggle-${b.id}`}
                    className={`border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider transition-colors ${b.active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>
                    {b.active ? "Active" : "Inactive"}
                  </button>
                </li>
              ))}
              {branches.length === 0 && <li className="py-4 text-sm text-slate-500">No branches.</li>}
            </ul>
            <form onSubmit={addBranch} className="mt-5 pt-5 border-t border-slate-100 space-y-3" data-testid="branch-form">
              <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500">Add Branch</p>
              <input required placeholder="Branch name" data-testid="branch-name-input" className="w-full border border-slate-300 px-3.5 py-2.5 text-sm rounded-sm focus:outline-none focus:border-[#C5221F]" value={branchForm.name} onChange={(e) => setBranchForm({ ...branchForm, name: e.target.value })} />
              <div className="grid grid-cols-3 gap-3">
                <input required type="number" step="any" placeholder="Latitude" data-testid="branch-lat-input" className="border border-slate-300 px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:border-[#C5221F]" value={branchForm.latitude} onChange={(e) => setBranchForm({ ...branchForm, latitude: e.target.value })} />
                <input required type="number" step="any" placeholder="Longitude" data-testid="branch-lng-input" className="border border-slate-300 px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:border-[#C5221F]" value={branchForm.longitude} onChange={(e) => setBranchForm({ ...branchForm, longitude: e.target.value })} />
                <input type="number" placeholder="Radius m" data-testid="branch-radius-input" className="border border-slate-300 px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:border-[#C5221F]" value={branchForm.radius_m} onChange={(e) => setBranchForm({ ...branchForm, radius_m: e.target.value })} />
              </div>
              <button type="submit" disabled={busy === "branch"} data-testid="branch-add-btn" className="bg-[#0A1128] hover:bg-[#16204a] disabled:opacity-60 text-white font-display font-semibold px-5 py-2.5 text-xs uppercase tracking-widest rounded-sm transition-colors inline-flex items-center gap-2">
                {busy === "branch" && <Loader2 size={12} className="animate-spin" />} Add Branch
              </button>
            </form>
          </div>
          <div className="bg-white border border-slate-200 p-6" data-testid="departments-card">
            <p className="flex items-center gap-2 font-mono-tech text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">
              <Layers size={14} className="text-[#C5221F]" /> Departments
            </p>
            <ul className="mt-4 divide-y divide-slate-100" data-testid="departments-list">
              {departments.map((dp) => (
                <li key={dp.id} className="py-3 text-sm" data-testid={`dept-row-${dp.id}`}>
                  <span className="font-medium text-slate-800">{dp.name}</span>
                  {dp.parent_name && <span className="text-xs text-slate-400"> · under {dp.parent_name}</span>}
                </li>
              ))}
              {departments.length === 0 && <li className="py-4 text-sm text-slate-500">No departments.</li>}
            </ul>
            <form onSubmit={addDept} className="mt-5 pt-5 border-t border-slate-100 space-y-3" data-testid="dept-form">
              <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500">Add Department</p>
              <input required placeholder="Department name" data-testid="dept-name-input" className="w-full border border-slate-300 px-3.5 py-2.5 text-sm rounded-sm focus:outline-none focus:border-[#C5221F]" value={deptForm.name} onChange={(e) => setDeptForm({ ...deptForm, name: e.target.value })} />
              <select data-testid="dept-parent-select" className="w-full border border-slate-300 px-3.5 py-2.5 text-sm rounded-sm bg-white focus:outline-none focus:border-[#C5221F]" value={deptForm.parent_id} onChange={(e) => setDeptForm({ ...deptForm, parent_id: e.target.value })}>
                <option value="">No parent (top level)</option>
                {departments.map((dp) => <option key={dp.id} value={dp.id}>{dp.name}</option>)}
              </select>
              <button type="submit" disabled={busy === "dept"} data-testid="dept-add-btn" className="bg-[#0A1128] hover:bg-[#16204a] disabled:opacity-60 text-white font-display font-semibold px-5 py-2.5 text-xs uppercase tracking-widest rounded-sm transition-colors inline-flex items-center gap-2">
                {busy === "dept" && <Loader2 size={12} className="animate-spin" />} Add Department
              </button>
            </form>
          </div>
        </div>
      )}
      {formError && <p className="mt-4 text-xs text-[#C5221F] bg-red-50 border border-red-200 px-3 py-2.5 rounded-sm" data-testid="branches-form-error">{formError}</p>}
    </div>
  );
};

export default Branches;
